/**
 * Multi-region frontend configuration.
 * Region is determined by REACT_APP_REGION env var (default: "cis").
 * The config is also fetched from backend /api/region at runtime.
 */

import { FEATURE_GOOGLE_AUTH } from "./featureFlags";

const REGION = process.env.REACT_APP_REGION || "cis";

// Static region configs (used before backend responds)
const REGIONS = {
  cis: {
    auth_providers: ["yandex", "email"],
    languages: ["ru"],
    default_language: "ru",
    domain: "medconcil.ru",
    brand_name: "MedConsilium",
    marketing_tagline: {
      en: "AI second opinion before you act",
      ru: "Второе мнение ИИ перед решением",
    },
    meta_description: {
      en: "MedConsilium — multiple medical AIs review your case. Get consensus, disagreements, and questions for your doctor. Informational only, not a diagnosis.",
      ru: "MedConsilium — несколько медицинских ИИ рассматривают ваш случай. Консенсус, расхождения и вопросы врачу. Только для информации, не диагноз.",
    },
    compliance: "fz152",
    currency: "RUB",
    compliance_label: { ru: "ФЗ-152", en: "FZ-152" },
    pii_label: { ru: "ПДн защищены", en: "PII Redacted" },
  },
  us: {
    auth_providers: ["google", "apple", "email"],
    languages: ["en", "es", "ar", "fr"],
    default_language: "en",
    domain: "app.medconcil.com",
    brand_name: "MedConcil",
    marketing_tagline: {
      en: "AI second opinion before you act",
      ru: "Второе мнение ИИ перед решением",
    },
    meta_description: {
      en: "MedConcil — multiple medical AIs review your case. Get consensus, disagreements, and questions for your doctor. Informational only, not a diagnosis.",
      ru: "MedConcil — несколько медицинских ИИ рассматривают ваш случай. Консенсус, расхождения и вопросы врачу. Только для информации, не диагноз.",
    },
    // Internal deployment key returned by /api/region. Not a user-facing claim:
    // direct consumer submissions are outside HIPAA. See the Privacy Policy.
    compliance: "hipaa",
    currency: "USD",
    pii_label: {
      en: "PII Redacted",
      es: "PII ocultos",
      ar: "تم إخفاء البيانات الشخصية",
      fr: "Données personnelles masquées",
    },
  },
};

export const regionConfig = REGIONS[REGION] || REGIONS.cis;
export const currentRegion = REGION;

/**
 * Pick localized copy from a { lang: text } map.
 * Fallback order: active language → region default → en → first available value.
 */
export function getLocalizedRegionText(textMap, language) {
  if (!textMap || typeof textMap !== "object") return "";
  const defaultLang = regionConfig.default_language || "en";
  const seen = new Set();
  const candidates = [language, defaultLang, "en", ...Object.keys(textMap)];
  for (const lang of candidates) {
    if (!lang || seen.has(lang)) continue;
    seen.add(lang);
    const value = textMap[lang];
    if (typeof value === "string" && value.trim()) return value;
  }
  return "";
}

// Currency conversion: backend stores costs in USD, CIS displays in RUB
const USD_TO_RUB = 95;

export const formatCost = (usdAmount, decimals = 2) => {
  if (usdAmount == null) return "\u2014";
  if (regionConfig.currency === "RUB") {
    const rub = Math.round(usdAmount * USD_TO_RUB);
    return `${rub} \u20BD`;
  }
  return `$${usdAmount.toFixed(decimals)}`;
};

// Ценообразование для клиники (временное, до внедрения биллинга)
const QUERY_PRICE_BASE = 89;        // базовая цена за запрос, ₽
const QUERY_PRICE_DOUBLE = 178;     // удвоенная цена, ₽
const COST_THRESHOLD_RUB = 60;      // порог фактических затрат, ₽

export const getQueryPrice = (costUsd) => {
  if (costUsd == null) return QUERY_PRICE_BASE;
  const costRub = costUsd * USD_TO_RUB;
  return costRub > COST_THRESHOLD_RUB ? QUERY_PRICE_DOUBLE : QUERY_PRICE_BASE;
};

export const formatQueryPrice = (costUsd) => {
  if (regionConfig.currency === "RUB") {
    return `${getQueryPrice(costUsd)} \u20BD`;
  }
  return formatCost(costUsd ?? 0);
};

/** Clinic billing display for dashboard revenue (CIS uses fixed query price). */
export const formatDashboardSpend = (metrics) => {
  if (regionConfig.currency === "RUB") {
    const queryPrice = getQueryPrice(metrics.avg_cost_per_query);
    const total = (metrics.queries_this_month || 0) * queryPrice;
    return `${total} \u20BD`;
  }
  return formatCost(metrics.total_spend ?? 0);
};

export const formatDashboardAvgCost = (metrics) => {
  if (regionConfig.currency === "RUB") {
    return `${getQueryPrice(metrics.avg_cost_per_query)} \u20BD`;
  }
  return formatCost(metrics.avg_cost_per_query ?? 0);
};

export const formatUserSpend = (user) => {
  if (regionConfig.currency === "RUB") {
    const avgCost = user.spend / Math.max(user.queries, 1);
    return `${(user.queries || 0) * getQueryPrice(avgCost)} \u20BD`;
  }
  return formatCost(user.spend ?? 0);
};

export const isUsRegion = () => currentRegion === "us";

export const validatePhone = (phone) => {
  if (!phone) return true;
  const normalized = phone.replace(/[\s\-()]/g, "");
  const pattern = currentRegion === "us" ? /^\+1\d{10}$/ : /^\+7\d{10}$/;
  return pattern.test(normalized);
};

export const getCurrencyLabel = () => {
  return regionConfig.currency === "RUB" ? "\u20BD" : "$";
};

export const convertToDisplayCurrency = (usdAmount) => {
  if (usdAmount == null) return 0;
  return regionConfig.currency === "RUB" ? usdAmount * USD_TO_RUB : usdAmount;
};

/**
 * Auth provider display configuration
 */
export const AUTH_PROVIDERS = {
  google: {
    id: "google",
    label: {
      en: "Sign in with Google",
      ru: "Войти через Google",
      es: "Iniciar sesión con Google",
      ar: "تسجيل الدخول عبر Google",
      fr: "Se connecter avec Google",
    },
    icon: "google",
    type: "oauth",
  },
  yandex: {
    id: "yandex",
    label: { en: "Sign in with Yandex", ru: "Войти через Яндекс" },
    icon: "yandex",
    type: "oauth",
  },
  apple: {
    id: "apple",
    label: {
      en: "Sign in with Apple",
      ru: "Войти через Apple",
      es: "Iniciar sesión con Apple",
      ar: "تسجيل الدخول عبر Apple",
      fr: "Se connecter avec Apple",
    },
    icon: "apple",
    type: "oauth",
  },
  email: {
    id: "email",
    label: {
      en: "Sign in with Email",
      ru: "Войти по Email",
      es: "Iniciar sesión con Email",
      ar: "تسجيل الدخول بالبريد الإلكتروني",
      fr: "Se connecter par e-mail",
    },
    icon: "email",
    type: "credentials",
  },
};

/**
 * Get auth providers for current region (respects temporary feature flags).
 */
export const getAuthProviders = () => {
  const providerIds = FEATURE_GOOGLE_AUTH
    ? regionConfig.auth_providers
    : regionConfig.auth_providers.filter((id) => id !== "google");
  return providerIds.map((id) => AUTH_PROVIDERS[id]).filter(Boolean);
};
